import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { ContractType } from "./admin-clients.js";
import { PrincipalMiddleware } from "./principal.js";
import { ShareFilterEntry } from "./saved-searches.js";
import { Forbidden } from "./shares.js";
import { PagingParams, pagedResponse } from "./paging.js";
import { LanguageSchema } from "./column-schemas.js";
/**
 * The sharing engine: client-level and user-level audience management for
 * files, online courses, examinations and folders, plus the saved-search
 * audience-rule surface (`share/items`) that drives measure/course/document
 * assignment. Every write here lands on `file_shares` / `online_course_shares`
 * / `examination_shares` / `folder_shares` / `location_shares` /
 * `client_shares` / `saved_searches` — none of those tables has a
 * standalone admin CRUD screen; this group is their only write path.
 */
const SelectedClient = S.Struct({ id: S.UUID });
const SelectedShare = S.Struct({ id: S.UUID });
const CountsResult = S.Struct({ created: S.Int, deleted: S.Int });
export const InvalidShareRequest = S.Struct({
    _tag: S.Literal("InvalidShareRequest"),
    reason: S.String,
});
export const SavedSearchNotFound = S.Struct({
    _tag: S.Literal("SavedSearchNotFound"),
});
// ---------------------------------------------------------------------------
// verify-item-clients — one item's client-level share fan-out across a
// caller-selected set of clients, plus the `client_shares` auto-assign rule
// that keeps future matching clients (by `share_all`/`client_types`) in
// sync. Cross-tenant by design (an item shared with many clients at once) —
// gated to OHS Admin only, never the per-client officer/location-manager
// roles `share-items`/`verify-client-shares`/`verify-user-shares` admit.
// ---------------------------------------------------------------------------
export const ShareCollection = S.Literal("file_shares", "online_course_shares", "examination_shares");
export const ShareItemType = S.Literal("online_course", "document", "examination");
export const VerifyItemClientsPayload = S.Struct({
    collection: ShareCollection,
    item_id: S.String,
    selected_clients: S.Array(SelectedClient),
    type: ShareItemType,
    share_all: S.optionalWith(S.Boolean, { default: () => false }),
    auto_assign: S.optionalWith(S.Boolean, { default: () => true }),
    client_types: S.optionalWith(S.Array(ContractType), { default: () => [] }),
});
export const VerifyItemClients = HttpApiEndpoint.post("verifyItemClients", "/admin/sharing/verify-item-clients")
    .setPayload(VerifyItemClientsPayload)
    .addSuccess(S.Struct({ data: CountsResult }))
    .addError(InvalidShareRequest, { status: 400 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// verify-client-shares — one client's item fan-out across a caller-selected
// set of items, diffed at translation-group granularity so picking one
// language variant shares every sibling translation.
// ---------------------------------------------------------------------------
export const VerifyClientSharesPayload = S.Struct({
    collection: ShareCollection,
    selected_shares: S.Array(SelectedShare),
    client_id: S.UUID,
    type: ShareItemType,
});
export const VerifyClientShares = HttpApiEndpoint.post("verifyClientShares", "/admin/sharing/verify-client-shares")
    .setPayload(VerifyClientSharesPayload)
    .addSuccess(S.Struct({ data: CountsResult }))
    .addError(InvalidShareRequest, { status: 400 })
    .addError(Forbidden, { status: 403 });
export const ClientCourseSelection = S.Struct({
    kind: S.Literal("online_course"),
    id: S.UUID,
    title: S.NullOr(S.String),
    language: S.NullOr(LanguageSchema),
    translation_group_id: S.NullOr(S.Int),
});
export const ClientDocumentSelection = S.Struct({
    kind: S.Literal("document"),
    id: S.UUID,
    /** The document's display name. Nullable in the estate, so a consumer showing it falls back to `filename_download`. */
    title: S.NullOr(S.String),
    filename_download: S.String,
    language: S.NullOr(LanguageSchema),
    translation_group_id: S.NullOr(S.Int),
});
export const GetClientAssetSelectionsParams = S.Struct({
    client_id: S.UUID,
    language: LanguageSchema,
});
export const GetClientAssetSelections = HttpApiEndpoint.get("getClientAssetSelections", "/admin/sharing/client-assets")
    .setUrlParams(GetClientAssetSelectionsParams)
    .addSuccess(S.Struct({
    courses: S.Array(ClientCourseSelection),
    documents: S.Array(ClientDocumentSelection),
}))
    .addError(Forbidden, { status: 403 });
export const AssetClientShareKind = S.Literal("document", "online_course", "folder", "examination");
export const GetAssetClientShareStateParams = S.Struct({
    kind: AssetClientShareKind,
    item_id: S.UUID,
});
const AssetShareClient = S.Struct({
    id: S.UUID,
    name: S.NullOr(S.String),
});
export const AssetClientShareState = S.Union(S.Struct({
    kind: S.Literal("all"),
    auto_assign: S.Boolean,
}), S.Struct({
    kind: S.Literal("client_types"),
    auto_assign: S.Boolean,
    client_types: S.Array(ContractType),
}), S.Struct({
    kind: S.Literal("clients"),
    auto_assign: S.Boolean,
    clients: S.Array(AssetShareClient),
}));
export const GetAssetClientShareState = HttpApiEndpoint.get("getAssetClientShareState", "/admin/sharing/asset-client-state")
    .setUrlParams(GetAssetClientShareStateParams)
    .addSuccess(S.Struct({ data: AssetClientShareState }))
    .addError(Forbidden, { status: 403 });
export const DearestShareSort = S.Literal("shared_with", "date_created", "access_granted", "shared_by");
export const ListDearestSharesParams = S.extend(PagingParams, S.Struct({
    client_id: S.UUID,
    search: S.optional(S.String),
    sort: S.optionalWith(DearestShareSort, {
        default: () => "date_created",
    }),
    direction: S.optionalWith(S.Literal("asc", "desc"), {
        default: () => "desc",
    }),
}));
export const DearestShareRow = S.Struct({
    id: S.UUID,
    user_id: S.UUID,
    user_first_name: S.NullOr(S.String),
    user_last_name: S.NullOr(S.String),
    user_email: S.NullOr(S.String),
    date_created: S.NullOr(S.String),
    shared_by: S.NullOr(S.UUID),
    shared_by_first_name: S.NullOr(S.String),
    shared_by_last_name: S.NullOr(S.String),
    access_granted: S.NullOr(S.Boolean),
});
export const ListDearestShares = HttpApiEndpoint.get("listDearestShares", "/admin/sharing/dearest-shares")
    .setUrlParams(ListDearestSharesParams)
    .addSuccess(pagedResponse(DearestShareRow))
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// verify-user-shares — one user's item fan-out (courses/documents at
// translation-group granularity, locations item-by-item).
// ---------------------------------------------------------------------------
export const UserShareCollection = S.Literal("online_course_shares", "file_shares", "location_shares");
export const UserShareType = S.Literal("online_course", "document", "location");
export const VerifyUserSharesPayload = S.Struct({
    collection: UserShareCollection,
    selected_shares: S.Array(SelectedShare),
    user_id: S.UUID,
    type: UserShareType,
    client_id: S.UUID,
});
export const VerifyUserShares = HttpApiEndpoint.post("verifyUserShares", "/admin/sharing/verify-user-shares")
    .setPayload(VerifyUserSharesPayload)
    .addSuccess(S.Struct({ data: CountsResult }))
    .addError(InvalidShareRequest, { status: 400 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// share-folder — share every file in a folder with a caller-selected set of
// clients in one action: a `client_shares` rule + `folder_shares` row per
// client, plus a per-file `client_shares` rule and client-level
// `file_shares` row for each file the folder currently holds. Cross-tenant
// like `verify-item-clients` — OHS Admin only.
// ---------------------------------------------------------------------------
export const ShareFolderPayload = S.Struct({
    folder_id: S.UUID,
    selected_clients: S.Array(SelectedClient),
    share_all: S.optionalWith(S.Boolean, { default: () => false }),
    client_types: S.optionalWith(S.Array(ContractType), { default: () => [] }),
    auto_assign: S.optionalWith(S.Boolean, { default: () => false }),
});
export const ShareFolderResult = S.Struct({
    success: S.Boolean,
    message: S.String,
    shared_files: S.Int,
    total_files: S.Int,
    folder_shares_created: S.Int,
    folder_shares_deleted: S.Int,
    file_shares_created: S.Int,
    file_shares_deleted: S.Int,
});
export const ShareFolder = HttpApiEndpoint.post("shareFolder", "/admin/sharing/share-folder")
    .setPayload(ShareFolderPayload)
    .addSuccess(S.Struct({ data: ShareFolderResult }))
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// share/items — resolve an audience from a typed `share_filter` (or
// `share_all`) and record it as the desired `saved_searches` audience.
// Updates synchronously revoke users dropped from that audience before the
// response; the outbox consumer then converges additions and any remaining
// differences through `saved-search.changed`.
// ---------------------------------------------------------------------------
export const ShareAudienceType = S.Literal("file_shares", "online_course_shares", "examination_shares", "measure_assignments", "dearest_shares", "folder_shares");
/**
 * The audiences a saved search can represent. `examination_shares` is not
 * one: `saved_searches.collection` has no member for examinations, so the
 * share-items endpoints refuse it at decode rather than at the column.
 */
export const SavedSearchAudienceType = S.Literal("file_shares", "online_course_shares", "measure_assignments", "dearest_shares", "folder_shares");
export const ShareItemsPayload = S.Struct({
    type: SavedSearchAudienceType,
    item_id: S.String,
    share_filter: S.Array(ShareFilterEntry),
    client_id: S.UUID,
    auto_assign: S.Boolean,
    share_all: S.Boolean,
});
export const ShareItemsResult = S.Struct({
    message: S.String,
    affected_users: S.Int,
});
export const ShareItems = HttpApiEndpoint.post("shareItems", "/admin/sharing/share-items")
    .setPayload(ShareItemsPayload)
    .addSuccess(S.Struct({ data: ShareItemsResult }))
    .addError(Forbidden, { status: 403 });
export const UpdateShareItemsPayload = S.Struct({
    search_id: S.UUID,
    type: SavedSearchAudienceType,
    item_id: S.String,
    share_filter: S.Array(ShareFilterEntry),
    client_id: S.UUID,
    auto_assign: S.Boolean,
    share_all: S.Boolean,
});
export const UpdateShareItems = HttpApiEndpoint.post("updateShareItems", "/admin/sharing/share-items/update")
    .setPayload(UpdateShareItemsPayload)
    .addSuccess(S.Struct({ data: ShareItemsResult }))
    .addError(SavedSearchNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// process-type-shares — re-run the `client_shares` auto-assign rules whose
// `selected_types` matches one client's current `contract_type` (e.g. after
// changing it), creating any client-level shares the rules now cover but the
// client doesn't have yet. Synchronous — the legacy endpoint fired this
// without awaiting it, so a caller's 200 OK never meant the work had
// actually finished; this intent returns the real counts instead.
// ---------------------------------------------------------------------------
export const ProcessTypeSharesPayload = S.Struct({ client_id: S.UUID });
export const ProcessTypeSharesResult = S.Struct({
    files_shared: S.Int,
    online_courses_shared: S.Int,
    examinations_shared: S.Int,
    folders_shared: S.Int,
});
export const ProcessTypeShares = HttpApiEndpoint.post("processTypeShares", "/admin/sharing/process-type-shares")
    .setPayload(ProcessTypeSharesPayload)
    .addSuccess(S.Struct({ data: ProcessTypeSharesResult }))
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// advanced-search — the share/assign audience picker: departments, job
// titles, users, locations, operational areas and non-admin roles matching
// a search string, scoped to one client and (for a location-manager caller)
// further narrowed to their own locations/users.
// ---------------------------------------------------------------------------
export const AudienceRowType = S.Literal("department", "job_title", "user", "location", "operational_area", "permission");
export const AudienceRow = S.Struct({
    id: S.String,
    name: S.String,
    type: AudienceRowType,
});
export const AdvancedSearchParams = S.Struct({
    search: S.NonEmptyString,
    client_id: S.UUID,
    limit: S.optionalWith(S.NumberFromString.pipe(S.int(), S.between(1, 100)), {
        default: () => 20,
    }),
});
export const AdvancedSearch = HttpApiEndpoint.get("advancedSearch", "/admin/sharing/advanced-search")
    .setUrlParams(AdvancedSearchParams)
    .addSuccess(S.Struct({ items: S.Array(AudienceRow) }))
    .addError(Forbidden, { status: 403 });
export const adminSharingGroup = HttpApiGroup.make("admin-sharing")
    .add(VerifyItemClients)
    .add(VerifyClientShares)
    .add(GetClientAssetSelections)
    .add(GetAssetClientShareState)
    .add(ListDearestShares)
    .add(VerifyUserShares)
    .add(ShareFolder)
    .add(ShareItems)
    .add(UpdateShareItems)
    .add(ProcessTypeShares)
    .add(AdvancedSearch)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=sharing.js.map